import { Box, Divider, Grid, Link } from '@mui/material';
import { SuccessButton } from 'components/atoms/ButtonsOld';
import { BasicInput, PasswordInput } from 'components/atoms/Inputs';
import { FormEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUsersLoginInfo } from 'redux/actions/internal/general/users';
import { RootState } from 'redux/reducers';
import PhoneInput from 'containers/login/components/PhoneInput';
import { useSignInProcess } from 'hooks/useSignInProcess';
import ValidationMessage from 'components/atoms/ValidationMessage';
import { useValidator } from 'services/validation/validator';
import AddressMap from 'components/molecules/AddressMap';
import Render from 'components/atoms/Render';
import { setValidationInfo } from 'redux/actions/internal/general/validation';
import useTranslation from 'next-translate/useTranslation';
import { useLoginUserProviders } from 'context/login-context';
import { useAuth } from 'context/authentication';
import { SmallText } from 'components/atoms/Typography';
import UserAddress from './UserAddress';

interface IRegisterInfoProps {
    loading?: boolean;
    onContinue: (event: FormEvent) => void;
}

const RegisterInfo = ({ onContinue, loading }: IRegisterInfoProps) => {
    const dispatch = useDispatch();

    const { t } = useTranslation('common');

    const signInProcess = useSignInProcess();
    const validator = useValidator();

    const usersLoginInfo = useSelector((state: RootState) => state.usersLoginInfo);

    const userProviders = useLoginUserProviders();

    const handleEmailChange = (email: string) => {
        dispatch(updateUsersLoginInfo({ email }));
    };

    const resetValidators = (type: string) => {
        if (validator.hasError('REGISTER_INFO', type)) {
            validator.resetValidation('REGISTER_INFO');
        }
        if (validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', type)) {
            validator.resetValidation('REGISTER_INFO_EMAIL_PASSWORD');
        }
    };

    const handlePasswordChange = (password: string) => {
        dispatch(updateUsersLoginInfo({ password }));
        resetValidators('password');
    };

    const handleNameChange = (name: string) => {
        dispatch(updateUsersLoginInfo({ name }));
        resetValidators('name');
    };

    const handleLastNameChange = (lastName: string) => {
        dispatch(updateUsersLoginInfo({ lastName }));
        resetValidators('lastName');
    };

    const handlePhoneOnChange = (code: string, phone: string) => {
        const _phone = code === '389' && phone.startsWith('0') ? phone.substring(1) : phone;
        dispatch(
            updateUsersLoginInfo({
                phone: {
                    countryCode: code,
                    phoneNumber: _phone,
                    fullNumber: `${code}${phone}`,
                },
            })
        );
        resetValidators('phone');
    };

    const handleAddressChange = (address: { fullAddress: string; latitude: number; longitude: number }) => {
        resetValidators('address');
        dispatch(updateUsersLoginInfo({ address }));
    };

    const handleContinue = async (e: FormEvent) => {
        e.preventDefault();
        let valid = false;
        if (usersLoginInfo.data.password || signInProcess.hasBeenThrough('LOGIN_WITH_EMAIL')) {
            valid = await validator.validate('REGISTER_INFO_EMAIL_PASSWORD', {
                email: usersLoginInfo.data.email,
                password: usersLoginInfo.data.password,
                name: usersLoginInfo.data.name,
                lastName: usersLoginInfo.data.lastName,
                phone: usersLoginInfo.data.phone.fullNumber,
            });
        } else {
            valid = await validator.validate('REGISTER_INFO', {
                email: usersLoginInfo.data.email,
                name: usersLoginInfo.data.name,
                lastName: usersLoginInfo.data.lastName,
                phone: usersLoginInfo.data.phone.fullNumber,
            });
        }

        if (!usersLoginInfo.data.address?.latitude || !usersLoginInfo.data.address?.latitude) {
            valid = false;
            dispatch(
                setValidationInfo({ hasError: true, message: 'Address is required', errorType: 'address' }, 'REGISTER_INFO')
            );
        }

        if (valid) {
            if (onContinue) onContinue(e);
        }
    };

    const handleHaveAccount = e => {
        e.preventDefault();
        signInProcess.goThrough([], 'LOGIN_WITH_PHONE');
    };

    return (
        <Box mt={3}>
            <form onSubmit={handleContinue} style={{ width: '100%' }}>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <Box fontSize="1em">
                            <BasicInput
                                placeholder={t('firstName')}
                                onChange={handleNameChange}
                                initialValue={usersLoginInfo.data.name}
                                error={
                                    validator.hasError('REGISTER_INFO', 'name') ||
                                    validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'name')
                                }
                            />
                            {validator.hasError('REGISTER_INFO', 'name') && <ValidationMessage type="REGISTER_INFO" />}
                            {validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'name') && (
                                <ValidationMessage type="REGISTER_INFO_EMAIL_PASSWORD" />
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box fontSize="1em">
                            <BasicInput
                                placeholder={t('lastName')}
                                onChange={handleLastNameChange}
                                initialValue={usersLoginInfo.data.lastName}
                                error={
                                    validator.hasError('REGISTER_INFO', 'lastName') ||
                                    validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'lastName')
                                }
                            />
                            {validator.hasError('REGISTER_INFO', 'lastName') && <ValidationMessage type="REGISTER_INFO" />}
                            {validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'lastName') && (
                                <ValidationMessage type="REGISTER_INFO_EMAIL_PASSWORD" />
                            )}
                        </Box>
                    </Grid>
                    {(signInProcess.hasBeenThrough('LOGIN_WITH_EMAIL') || signInProcess.hasBeenThrough('SOCIAL_GOOGLE')) && (
                        <Grid item xs={12} zIndex={999999}>
                            <Box>
                                <PhoneInput
                                    onChange={handlePhoneOnChange}
                                    defaultCountryCode={usersLoginInfo.data.phone.countryCode}
                                    defaultPhoneNumber={usersLoginInfo.data.phone.phoneNumber}
                                    countryCode={usersLoginInfo.data.phone.countryCode}
                                    phoneNumber={usersLoginInfo.data.phone.phoneNumber}
                                    onEnterKeyPress={handleContinue}
                                    error={
                                        validator.hasError('REGISTER_INFO', 'phone') ||
                                        validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'phone')
                                    }
                                    controlled
                                />
                                {validator.hasError('REGISTER_INFO', 'phone') && <ValidationMessage type="REGISTER_INFO" />}
                                {validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'phone') && (
                                    <ValidationMessage type="REGISTER_INFO_EMAIL_PASSWORD" />
                                )}
                            </Box>
                        </Grid>
                    )}
                    <div id="recaptcha-container"></div>
                    <Grid item xs={12}>
                        <Box mt={1}>
                            {/* <AddressMap address={usersLoginInfo.data.address} onAddressChange={handleAddressChange} /> */}
                            <UserAddress address={usersLoginInfo.data.address} onAddressChange={handleAddressChange} />
                        </Box>
                        {validator.hasError('REGISTER_INFO', 'address') && <ValidationMessage type="REGISTER_INFO" />}
                        <Box mt={2}>
                            <Divider />
                        </Box>
                    </Grid>
                    <Render when={signInProcess.hasBeenThrough('LOGIN_WITH_EMAIL')}>
                        <Grid item xs={12}>
                            <Box mt={1}>
                                <BasicInput
                                    name="email"
                                    placeholder={t('email')}
                                    onChange={handleEmailChange}
                                    initialValue={usersLoginInfo.data.email}
                                    error={
                                        validator.hasError('REGISTER_INFO', 'email') ||
                                        validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'email')
                                    }
                                />
                                {validator.hasError('REGISTER_INFO', 'email') && <ValidationMessage type="REGISTER_INFO" />}
                                {validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'email') && (
                                    <ValidationMessage type="REGISTER_INFO_EMAIL_PASSWORD" />
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box mt={1}>
                                <PasswordInput
                                    name="password"
                                    placeholder={t('password')}
                                    onChange={handlePasswordChange}
                                    initialValue={usersLoginInfo.data.password}
                                    error={validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'password')}
                                />
                                {validator.hasError('REGISTER_INFO_EMAIL_PASSWORD', 'password') && (
                                    <ValidationMessage type="REGISTER_INFO_EMAIL_PASSWORD" />
                                )}
                            </Box>
                        </Grid>
                    </Render>
                    <Grid item xs={12}>
                        <Box mt={2}>
                            <SuccessButton loading={loading}>{t('continue')}</SuccessButton>
                            <Box mt={1}>
                                <Grid container justifyContent="center">
                                    {/* <ValidationMessage type="PHONE_VERIFICATION" /> */}
                                    {validator.hasError('REGISTER_INFO', 'global') && <ValidationMessage type="REGISTER_INFO" />}
                                    {validator.hasError('PHONE_VERIFICATION', 'global') && (
                                        <ValidationMessage type="PHONE_VERIFICATION" />
                                    )}
                                </Grid>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                mt: { xs: 3, lg: 0 },
                            }}
                        >
                            <Grid item xs={12} sm={6}>
                                <Link onClick={handleHaveAccount} variant="body2" sx={{ cursor: 'pointer' }}>
                                    <SmallText color="primary.main">{t('alreadyHaveAccount')}</SmallText>
                                </Link>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default RegisterInfo;
